import { useHistory } from 'react-router';
import { Dispatch, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import styles from './PassportMassCancellation.module.scss';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import {
  ROUTE_PASSPORT_MASS_CANCELLATION_GROUP_BY_ID,
  ROUTE_PASSPORT_MASS_CANCELLATION_NEW_GROUP,
} from '../../../CorePackage/MainModule/routes/routes';
import { getUserRoleIsProsecutor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import {
  passportGroupGetNextPageAction,
  passportGroupGetPageAction,
  passportGroupSetNoticeAction,
} from '../../../PassportGroupPackage/MainModule/actions/passportGroupActions';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import {
  passportGroupListSelector,
  passportGroupListTotalCountSelector,
  passportGroupNoticeSelector,
} from '../../../PassportGroupPackage/MainModule/selectors/passportGroupSelector';
import { ISelectOption, SelectInput } from '../../../UIPackage/MainModule/components/SelectInput';
import { getPassportMassCancelReasonSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { catalogForceGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { IPassportGroupShort } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupShort';
import { PassportGroupStatusEnum } from '../../../APIPackage/PassportGroupsModule/enums/PassportGroupStatusEnum';
import { PassportGroupTypeEnum } from '../../../APIPackage/PassportGroupsModule/enums/PassportGroupTypeEnum';

const canBeOpen = (group: IPassportGroupShort) => {
  return !(group.status === PassportGroupStatusEnum.DRAFT && group.processing);
};

export const PassportMassCancellation = () => {
  const history = useHistory();
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const notice = useSelector(passportGroupNoticeSelector);
  const isProsecutor = useSelector(getUserRoleIsProsecutor);
  const groupList = useSelector(passportGroupListSelector);
  const totalCount = useSelector(passportGroupListTotalCountSelector);
  const reasonSelectOptions = useSelector(getPassportMassCancelReasonSelectOptions);
  const [reasonValue, setReasonValue] = useState<ISelectOption<number> | undefined>();

  useEffect(() => {
    dispatch(catalogForceGet(CommonCatalogsNamesEnum.PASSPORT_MASS_CANCEL_REASON));
    setReasonValue(undefined);
  }, [dispatch, setReasonValue]);

  useEffect(() => {
    dispatch(passportGroupGetPageAction(PassportGroupTypeEnum.CANCELLATION, reasonValue?.id));
  }, [dispatch, reasonValue]);

  const handleNextPage = useCallback(() => {
    dispatch(passportGroupGetNextPageAction(PassportGroupTypeEnum.CANCELLATION, reasonValue?.id));
  }, [dispatch, reasonValue]);

  const handleSearchButtonClick = useCallback(() => {
    dispatch(passportGroupGetPageAction(PassportGroupTypeEnum.CANCELLATION, reasonValue?.id));
  }, [dispatch, reasonValue]);

  const handleCreateGroup = useCallback(() => {
    history.push(ROUTE_PASSPORT_MASS_CANCELLATION_NEW_GROUP);
  }, [history]);

  useEffect(() => {
    if (notice) {
      dispatch(
        addNoticeAction({
          renderContent: notice,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.PASSPORT_GROUP,
        }),
      );
      dispatch(passportGroupSetNoticeAction(undefined));
    }
  }, [dispatch, notice]);

  return (
    <div>
      <div className={styles.Header}>
        <h1 className={styles.Title}>Группа мероприятий, подлежащих отмене</h1>
        {isProsecutor && (
          <Button text="Сформировать группу" type="button" color="primary" onClick={handleCreateGroup} />
        )}
      </div>
      <div className={styles.FilterBlock}>
        <SelectInput
          placeholder="Для поиска выберите основание массовой отмены"
          options={reasonSelectOptions}
          value={reasonValue}
          onChange={setReasonValue}
          isClearable
        />
        <Button text="Искать" type="button" color="primary" onClick={handleSearchButtonClick} />
      </div>
      <table className={styles.Table}>
        <thead>
          <tr className={styles.TableHeader}>
            <th>№ группы</th>
            <th>дата и время создания</th>
            <th>создано (наименование прокуратуры)</th>
            <th>основания массовой отмены</th>
            <th>статус</th>
            <th>всего</th>
            <th>отменено</th>
          </tr>
        </thead>
        <tbody>
          {groupList.map(item => (
            <tr key={item.id} className={styles.TableRow}>
              <td>
                {canBeOpen(item) ? (
                  <Link className="shared-table-link" to={ROUTE_PASSPORT_MASS_CANCELLATION_GROUP_BY_ID(item.id)}>
                    {item.id}
                  </Link>
                ) : (
                  item.id
                )}
              </td>
              <td>{item.createdAt}</td>
              <td>{item.prosecutorOrganizationName}</td>
              <td>{item.reasonName}</td>
              <td>
                {item.statusName}
                {!item.error && item.processing && '(в процессе)'}
                {item.error && (
                  <div className={styles.ErrorTooltip}>
                    <span className={styles.ErrorIcon}></span>
                    <div className={styles.TooltipBlock}>
                      <div className={styles.TooltipBlockContent}>
                        <span>При выполнении операции произошла ошибка.</span>
                        <span>Обратитесь в СТП.</span>
                      </div>
                    </div>
                  </div>
                )}
              </td>
              <td>{item.totalCount}</td>
              <td>{item.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalCount > groupList.length && <Button text="Показать ещё" color="load" onClick={handleNextPage} />}
    </div>
  );
};
